import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = {
  name: "", instagram: "", order: 0, photo: "",
  specialty: { pt: "", en: "", de: "" },
  bio: { pt: "", en: "", de: "" },
};

const I18nInputs = ({ label, value, onChange, textarea, prefix }) => (
  <div className="flex flex-col gap-2">
    <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">{label}</span>
    {["pt", "en", "de"].map((l) =>
      textarea ? (
        <textarea key={l} rows={2} value={value[l]} onChange={(e) => onChange({ ...value, [l]: e.target.value })}
          placeholder={`${label} (${l.toUpperCase()})`} data-testid={`${prefix}-${l}`}
          className="bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white transition-colors resize-y" />
      ) : (
        <input key={l} value={value[l]} onChange={(e) => onChange({ ...value, [l]: e.target.value })}
          placeholder={`${label} (${l.toUpperCase()})`} data-testid={`${prefix}-${l}`}
          className="bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white transition-colors" />
      )
    )}
  </div>
);

export { I18nInputs };

export const ArtistsTab = ({ artists, refresh }) => {
  const [form, setForm] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/artists/${form.id}`, form);
      else await api.post("/artists", { ...form, order: artists.length });
      toast.success("Artista salvo");
      setForm(null);
      refresh();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este artista?")) return;
    await api.delete(`/artists/${id}`);
    toast.success("Artista removido");
    refresh();
  };

  return (
    <div data-testid="admin-artists-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display uppercase text-3xl tracking-wider">Artistas ({artists.length})</h2>
        <button onClick={() => setForm({ ...empty })} data-testid="artist-add-btn"
          className="inline-flex items-center gap-2 bg-white text-black font-mono2 text-[10px] tracking-[0.2em] uppercase px-4 py-2 hover:bg-[#E63946] hover:text-white transition-colors">
          <Plus size={14} /> Adicionar
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="border border-white/15 p-6 mb-8 grid gap-4 bg-[#121214]" data-testid="artist-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome"
              data-testid="artist-name-input" className="bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white" />
            <input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="Instagram (URL)"
              data-testid="artist-instagram-input" className="bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white" />
          </div>
          <I18nInputs label="Especialidade" prefix="artist-specialty" value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} />
          <I18nInputs label="Bio" prefix="artist-bio" textarea value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} />
          <ImageUpload value={form.photo} onChange={(p) => setForm({ ...form, photo: p })} testid="artist-photo" />
          <div className="flex gap-3">
            <button type="submit" data-testid="artist-save-btn"
              className="bg-white text-black font-mono2 text-[10px] tracking-[0.2em] uppercase px-6 py-3 hover:bg-[#E63946] hover:text-white transition-colors">Salvar</button>
            <button type="button" onClick={() => setForm(null)} data-testid="artist-cancel-btn"
              className="border border-white/20 font-mono2 text-[10px] tracking-[0.2em] uppercase px-6 py-3 hover:border-white transition-colors">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid gap-2">
        {artists.map((a) => (
          <div key={a.id} className="flex items-center gap-4 border border-white/10 p-3" data-testid={`artist-row-${a.id}`}>
            <img src={fileUrl(a.photo)} alt="" className="w-12 h-12 object-cover img-bw" />
            <div className="flex-1 min-w-0">
              <p className="font-display uppercase text-xl tracking-wider truncate">{a.name}</p>
              <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 truncate">{a.specialty?.pt}</p>
            </div>
            <button onClick={() => setForm({ ...empty, ...a })} data-testid={`artist-edit-${a.id}`} aria-label="editar"
              className="border border-white/15 p-2 hover:bg-white hover:text-black transition-colors"><Pencil size={14} /></button>
            <button onClick={() => remove(a.id)} data-testid={`artist-delete-${a.id}`} aria-label="remover"
              className="border border-white/15 p-2 hover:bg-[#E63946] hover:border-[#E63946] transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
